import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Bell,
  CircleDollarSign,
  FileSpreadsheet,
  FileText,
  Layers,
  Percent,
  Zap,
} from "lucide-react";
import DangerZoneCard from "../../../components/admin/DangerZoneCard";
import ResetBusinessDataModal from "../../../components/admin/ResetBusinessDataModal";
import { api } from "../../../lib/axios";
import { getPlansAbonnement, type PlanAbonnement } from "../../../lib/api/plansAbonnement";
import type {
  CommercialConfigSummary,
  ConfigurationAlert,
  SubscriptionPlanSummary,
} from "./parametresDashboardTypes";

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function parseUpdatedAt(r: Record<string, unknown>): string | null {
  const u = r.updatedAt;
  if (typeof u !== "string" || !u.trim()) return null;
  const d = new Date(u);
  return Number.isNaN(d.getTime()) ? null : u;
}

function formatDt(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function bankConfiguredFromConfig(c: Record<string, unknown>): boolean {
  const keys = [
    "banque",
    "nomCompte",
    "numeroCompte",
    "iban",
    "swift",
    "mobileMoneyEntreprise",
  ] as const;
  return keys.some((k) => {
    const v = c[k];
    return typeof v === "string" && v.trim().length > 0;
  });
}

function buildCommercialSummary(c: Record<string, unknown> | null): CommercialConfigSummary {
  if (!c) {
    return {
      tvaLabel: "Non définie",
      deviseLabel: "Non définie",
      bankConfigured: false,
      lastModified: null,
    };
  }
  const tRaw = c.tauxTva ?? c.tauxTVA;
  const tNum =
    typeof tRaw === "number"
      ? tRaw
      : typeof tRaw === "string"
        ? Number(String(tRaw).replace(",", "."))
        : NaN;
  const tvaLabel = Number.isFinite(tNum) ? `${tNum} %` : "Non définie";

  const deviseRaw = c.deviseDefaut ?? c.devise;
  const deviseStr = typeof deviseRaw === "string" ? deviseRaw.trim() : "";
  const deviseLabel = deviseStr.length > 0 ? deviseStr : "Non définie";

  return {
    tvaLabel,
    deviseLabel,
    bankConfigured: bankConfiguredFromConfig(c),
    lastModified: parseUpdatedAt(c),
  };
}

function buildPlansSummary(plans: PlanAbonnement[]): SubscriptionPlanSummary {
  const totalCount = plans.length;
  const activeCount = plans.filter((p) => p.actif !== false).length;

  let latestIso: string | null = null;
  for (const p of plans) {
    const u = p.updatedAt;
    if (typeof u !== "string" || !u.trim()) continue;
    const t = new Date(u).getTime();
    if (Number.isNaN(t)) continue;
    if (!latestIso || new Date(latestIso).getTime() < t) latestIso = u;
  }

  return {
    activeCount,
    totalCount,
    mostUsedPlanName: null,
    lastModified: latestIso,
  };
}

function buildAlerts(
  configOk: boolean,
  plansOk: boolean,
  config: Record<string, unknown> | null,
  plans: PlanAbonnement[]
): ConfigurationAlert[] {
  const alerts: ConfigurationAlert[] = [];
  if (!configOk || !config) {
    return alerts;
  }

  const tRaw = config.tauxTva ?? config.tauxTVA;
  const tNum =
    typeof tRaw === "number"
      ? tRaw
      : typeof tRaw === "string"
        ? Number(String(tRaw).replace(",", "."))
        : NaN;
  if (!Number.isFinite(tNum)) {
    alerts.push({
      id: "tva_missing",
      message: "TVA non définie dans la configuration commerciale.",
    });
  }

  const deviseRaw = config.deviseDefaut ?? config.devise;
  const deviseStr = typeof deviseRaw === "string" ? deviseRaw.trim() : "";
  if (!deviseStr) {
    alerts.push({
      id: "devise_missing",
      message: "Devise par défaut non définie.",
    });
  }

  if (!bankConfiguredFromConfig(config)) {
    alerts.push({
      id: "bank_missing",
      message: "Aucun compte bancaire configuré (banque, IBAN, etc.).",
    });
  }

  if (plansOk) {
    const activePlans = plans.filter((p) => p.actif !== false).length;
    if (activePlans === 0) {
      alerts.push({
        id: "no_active_plans",
        message: "Aucun plan d’abonnement actif.",
      });
    }
  }

  const pf = String(config.numerotationFacturePrefix ?? config.prefixeFacture ?? "").trim();
  if (!pf) {
    alerts.push({
      id: "prefix_facture",
      message: "Aucun préfixe facture défini.",
    });
  }
  const pp = String(config.numerotationProformaPrefix ?? config.prefixeProforma ?? "").trim();
  if (!pp) {
    alerts.push({
      id: "prefix_proforma",
      message: "Aucun préfixe proforma défini.",
    });
  }
  const pq = String(config.numerotationQuittancePrefix ?? config.prefixeQuittance ?? "").trim();
  if (!pq) {
    alerts.push({
      id: "prefix_quittance",
      message: "Aucun préfixe quittance défini.",
    });
  }

  return alerts;
}

const cardClass =
  "rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-xl ring-1 ring-white/5";

const ParametresPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [configUnavailable, setConfigUnavailable] = useState(false);
  const [plansUnavailable, setPlansUnavailable] = useState(false);
  const [configRaw, setConfigRaw] = useState<Record<string, unknown> | null>(null);
  const [plans, setPlans] = useState<PlanAbonnement[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setConfigUnavailable(false);
    setPlansUnavailable(false);

    let cfg: Record<string, unknown> | null = null;
    let plansList: PlanAbonnement[] = [];

    const confPromise = api.get<unknown>("/configuration").then((res) => res.data);
    const plansPromise = getPlansAbonnement();

    const [cr, pr] = await Promise.allSettled([confPromise, plansPromise]);

    if (cr.status === "fulfilled" && isRecord(cr.value)) {
      cfg = cr.value;
    } else {
      setConfigUnavailable(true);
    }

    if (pr.status === "fulfilled") {
      plansList = Array.isArray(pr.value) ? pr.value : [];
    } else {
      setPlansUnavailable(true);
      plansList = [];
    }

    setConfigRaw(cfg);
    setPlans(plansList);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const commercial = useMemo(() => buildCommercialSummary(configRaw), [configRaw]);
  const plansSummary = useMemo(() => buildPlansSummary(plans), [plans]);

  const alerts = useMemo(
    () => buildAlerts(!configUnavailable, !plansUnavailable, configRaw, plans),
    [configUnavailable, plansUnavailable, configRaw, plans]
  );

  const dataPartialWarning =
    configUnavailable || plansUnavailable || loading ? (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/95">
        Certaines données de configuration sont indisponibles. Les indicateurs ci-dessous peuvent être
        incomplets.
      </div>
    ) : null;

  const showCompleteBadge =
    !loading &&
    !configUnavailable &&
    !plansUnavailable &&
    alerts.length === 0;

  return (
    <div className="min-h-0 w-full bg-slate-950 px-4 py-6 text-slate-100 md:px-6 md:py-8">
      <div className="w-full max-w-none space-y-6">
        <motion.header
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-2"
        >
          <h1 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">Paramètres</h1>
          <p className="max-w-2xl text-sm text-slate-400">
            Configurez votre facturation et vos offres.
          </p>
        </motion.header>

        {dataPartialWarning}

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Documents commerciaux */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.05 }}
            className={cardClass}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/25">
                  <FileText className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <h2 className="text-base font-semibold text-white">Documents commerciaux</h2>
                  <p className="text-xs text-slate-500">Factures, proformas, quittances</p>
                </div>
              </div>
              <Link
                to="/admin/parametres/documents-commerciaux"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white shadow hover:bg-emerald-500"
              >
                Modifier
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <dl className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-3 py-2.5">
                <dt className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  <Percent className="h-3.5 w-3.5" />
                  TVA actuelle
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-100">{commercial.tvaLabel}</dd>
              </div>
              <div className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-3 py-2.5">
                <dt className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  <CircleDollarSign className="h-3.5 w-3.5" />
                  Devise par défaut
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-100">{commercial.deviseLabel}</dd>
              </div>
              <div className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-3 py-2.5 sm:col-span-2">
                <dt className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  <Banknote className="h-3.5 w-3.5" />
                  Compte bancaire configuré
                </dt>
                <dd className="mt-1 text-sm font-medium text-slate-100">
                  {commercial.bankConfigured ? (
                    <span className="text-emerald-400">Oui</span>
                  ) : (
                    <span className="text-slate-400">Non configuré</span>
                  )}
                </dd>
              </div>
            </dl>
            <p className="mt-4 text-xs text-slate-500">
              Dernière modification :{" "}
              <span className="text-slate-400">
                {formatDt(commercial.lastModified) ?? "—"}
              </span>
            </p>
          </motion.section>

          {/* Plans d'abonnement */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1 }}
            className={cardClass}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500/15 text-sky-400 ring-1 ring-sky-500/25">
                  <Layers className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <h2 className="text-base font-semibold text-white">Plans d’abonnement</h2>
                  <p className="text-xs text-slate-500">Catalogue tarifs / offres</p>
                </div>
              </div>
              <Link
                to="/admin/parametres/plans-abonnement"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700"
              >
                Gérer
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <dl className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-3 py-2.5">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  Plans actifs
                </dt>
                <dd className="mt-1 text-lg font-semibold tabular-nums text-white">
                  {loading ? "…" : plansSummary.activeCount}
                </dd>
              </div>
              <div className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-3 py-2.5">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  Total plans
                </dt>
                <dd className="mt-1 text-lg font-semibold tabular-nums text-white">
                  {loading ? "…" : plansSummary.totalCount}
                </dd>
              </div>
              <div className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-3 py-2.5 sm:col-span-2">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  Plan le plus utilisé
                </dt>
                <dd className="mt-1 text-sm text-slate-300">
                  {plansSummary.mostUsedPlanName ?? (
                    <span className="italic text-slate-500">Données non exposées par l’API</span>
                  )}
                </dd>
              </div>
            </dl>
            {!loading && plansSummary.activeCount === 0 ? (
              <p className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs text-amber-200/90">
                Aucun plan actif
              </p>
            ) : null}
            <p className="mt-4 text-xs text-slate-500">
              Dernière modification (catalogue) :{" "}
              <span className="text-slate-400">
                {formatDt(plansSummary.lastModified) ?? "—"}
              </span>
            </p>
          </motion.section>
        </div>

        {/* Alertes */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15 }}
          className={cardClass}
        >
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-400/90" />
            <h2 className="text-base font-semibold text-white">Alertes configuration</h2>
          </div>
          {loading ? (
            <p className="mt-4 text-sm text-slate-500">Chargement…</p>
          ) : alerts.length > 0 ? (
            <ul className="mt-4 space-y-2">
              {alerts.map((a) => (
                <li
                  key={a.id}
                  className="flex gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-sm text-amber-100/95"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                  {a.message}
                </li>
              ))}
            </ul>
          ) : showCompleteBadge ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/35 bg-emerald-500/10 px-3 py-1.5 text-sm font-medium text-emerald-200">
              Configuration commerciale complète
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-500">
              Vérifiez la connexion API pour valider l’ensemble des règles.
            </p>
          )}
        </motion.section>

        {/* Actions rapides */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.2 }}
          className={cardClass}
        >
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-emerald-400/90" />
            <h2 className="text-base font-semibold text-white">Actions rapides</h2>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/admin/parametres/documents-commerciaux"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-2.5 text-sm font-medium text-slate-100 hover:border-emerald-500/40 hover:bg-slate-900"
            >
              <FileText className="h-4 w-4 text-emerald-400" />
              Modifier documents commerciaux
            </Link>
            <Link
              to="/admin/parametres/plans-abonnement"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-2.5 text-sm font-medium text-slate-100 hover:border-emerald-500/40 hover:bg-slate-900"
            >
              <CircleDollarSign className="h-4 w-4 text-sky-400" />
              Ajouter plan d’abonnement
            </Link>
            <Link
              to="/admin/finance/factures"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-2.5 text-sm font-medium text-slate-100 hover:border-emerald-500/40 hover:bg-slate-900"
            >
              <FileSpreadsheet className="h-4 w-4 text-slate-300" />
              Voir factures
            </Link>
            <Link
              to="/admin/finance/proformas"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-2.5 text-sm font-medium text-slate-100 hover:border-emerald-500/40 hover:bg-slate-900"
            >
              <FileText className="h-4 w-4 text-slate-300" />
              Voir proformas
            </Link>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.25 }}
          className="max-w-4xl"
        >
          <DangerZoneCard>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-rose-100">Réinitialiser les données métier de test</h3>
                <p className="mt-1 max-w-xl text-xs text-slate-400">
                  Supprime les clients, contrats et passages identifiés comme jeu E2E (références CLI-E2E-*, tournée
                  seed). Utile pour repartir d’un état propre avant de relancer{" "}
                  <code className="rounded bg-slate-900 px-1 py-0.5 text-[10px] text-slate-300">npm run seed:e2e</code>{" "}
                  côté serveur.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setResetModalOpen(true)}
                className="inline-flex shrink-0 items-center justify-center rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-rose-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-400"
              >
                Réinitialiser les données métier
              </button>
            </div>
          </DangerZoneCard>
        </motion.section>

        <ResetBusinessDataModal open={resetModalOpen} onClose={() => setResetModalOpen(false)} />
      </div>
    </div>
  );
};

export default ParametresPage;
